import styled, { css } from 'styled-components';
import Icon from '../../../_shared/components/Icon';

import Badge from '../../../_shared/components/Badge';
import Label from '../../../_shared/components/Label';

/**
 * Variants
 */
const thumbnailImageVariant = {
  active: css`
    border: 2px solid ${({ theme }) => theme.COLORS.BASE};
  `
};

const videoOverlayVariant = {
  true: css`
    opacity: 1;
    visibility: visible;
  `,
  false: css`
    opacity: 0;
    visibility: hidden;
  `
};

/**
 * Gallery Header
 */
const headerHeight = '60px';
const Header = styled.div`
  display: flex;
  width: 100%;
  height: ${headerHeight};
  background-color: ${({ theme }) => theme.COLORS.BASE};
  margin: 0;
  padding: 0 36px;
  justify-content: space-between;
`;

Header.Title = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
`;

Header.Controls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
`;

export { Header };

export const Year = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.BASE_INVERTED};
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  padding: 0 4px;
  line-height: normal;
  border-radius: 5.5px;
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
`;

export const Make = styled(Label.Light)`
  white-space: nowrap;
  display: inline-block;
  padding: 0 12px;
  vertical-align: middle;
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
`;

export const Model = styled(Label.Bold)`
  display: block;
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
`;

export const Esc = styled.div`
  width: 31px;
  height: 24px;
  background: ${({ theme }) => theme.COLORS.NEUTRAL_20};
  color: ${({ theme }) => theme.COLORS.BASE};
  border-radius: 3px;
  margin: 0 6px;
  padding-bottom: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;

  &:after {
    content: '';
    width: calc(100% - 4px);
    height: 2px;
    background: ${({ theme }) => theme.COLORS.BASE};
    position: absolute;
    bottom: 1px;
    left: 2px;
    border-radius: 0 0 4px 4px;
    border-top: 1px solid ${({ theme }) => theme.COLORS.NEUTRAL_20};
  }
`;

const CloseButton = styled.button`
  width: 88px;
  height: 30px;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  border: 2px solid ${({ theme }) => theme.COLORS.BASE_INVERTED};
  border-radius: 4px;
  letter-spacing: ${({ theme }) => theme.LETTER_SPACING.WIDE};
  margin-left: 9px;
  padding: 0;
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
    color: ${({ theme }) => theme.COLORS.BASE};

    path {
      stroke: ${({ theme }) => theme.COLORS.BASE};
      stroke-width: 4px;
    }
  }

  &:focus {
    outline: none;
    border: 0;
  }
`;

CloseButton.Icon = styled(Icon).attrs({
  icon: 'TR5-Close_X'
})`
  width: 11px;
  height: 11px;
  margin-left: 4px;

  path {
    stroke: ${({ theme }) => theme.COLORS.BASE_INVERTED};
    stroke-width: 2px;
  }
`;

export { CloseButton };

/**
 * Gallery Content
 */
const Grid = styled.div`
  display: grid;
  overflow: none;
  background-color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  width: 100%;
  height: calc(100vh - ${headerHeight});
  margin: 0;
  padding: 0;
  grid-template-areas: 'thumbnail content';
  grid-template-columns: 336px 1fr;
  color: ${({ theme }) => theme.COLORS.BASE};
`;

Grid.Thumbnails = styled.div`
  grid-area: 'thumbnail';
  overflow-y: auto;
  overflow-x: hidden;
  padding: 24px 36px;
  position: relative;

  &::-webkit-scrollbar {
    width: 18px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    width: 6px;
    background: ${({ theme }) => theme.COLORS.BASE};
    border: 6px solid ${({ theme }) => theme.COLORS.BASE_INVERTED};
    border-radius: 10px;
  }
`;

Grid.Content = styled.div`
  grid-area: 'content';
  overflow: none;
  position: relative;
`;

export { Grid };

const arrowSize = 41;
const GalleryArrow = styled.div`
  width: ${`${arrowSize}px`};
  height: ${`${arrowSize}px`};
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  position: absolute;
  z-index: 10010;
  top: calc(50vh - ${headerHeight} - ${`${arrowSize / 2}px`});
  ${({ variant = 'left' }) => variant}: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 6px rgba(0, 0, 0, 0.25);
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: ${({ theme }) => theme.COLORS.BASE};
    cursor: pointer;
  }
`;

GalleryArrow.Left = styled(Icon).attrs({
  icon: 'TR5-Prev_chev'
})`
  width: 24px;
  margin-right: 3px;
`;

GalleryArrow.Right = styled(Icon).attrs({
  icon: 'TR5-Next_chev'
})`
  width: 24px;
  margin-left: 3px;
`;

export { GalleryArrow };

/**
 * Thumbnails
 */
export const ThumbnailShadow = styled.div`
  width: 318px;
  height: 55px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.8) 50%, transparent);
  position: fixed;
  left: 0;
  bottom: 0;
  z-index: 10010;
`;

export const ThumbnailHeader = styled.div`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.XXLARGE};
  line-height: 36px;
  margin-bottom: 24px;
`;

export const ThumbnailSection = styled.div`
  padding-bottom: 30px;
`;

export const ThumbnailSubHeader = styled.div`
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  line-height: 130%;
  padding-bottom: 6px;
`;

export const ThumbnailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 80px);
  grid-template-rows: minmax(60px, auto);
  grid-gap: 6px;
`;

export const ThumbnailWrapper = styled.div`
  position: relative;
  line-height: 0;
`;

export const ThumbnailDisclosureCount = styled.div`
  width: 23px;
  height: 23px;
  position: absolute;
  top: 6px;
  left: 6px;
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  background: ${({ theme }) => theme.COLORS.RED};
  color: ${({ theme }) => theme.COLORS.BASE};
  border: 1px solid ${({ theme }) => theme.COLORS.BASE};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 0px 8px ${({ theme }) => theme.COLORS.BASE_INVERTED};
  cursor: pointer;
`;

export const ThumbnailImage = styled.img`
  width: 80px;
  height: 60px;
  cursor: pointer;

  ${({ variant }) => thumbnailImageVariant[variant]}
`;

export const ThumbnailVideo = styled.video`
  width: 80px;
  height: 60px;
  cursor: pointer;

  ${({ variant }) => thumbnailImageVariant[variant]}
`;

/**
 * Content
 */
const Content = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

Content.FluidContainer = styled.div`
  position: relative;
  line-height: 0;
`;

Content.Overlay = styled.div`
  width: 100%;
  height: 100px;
  padding: 26px 24px 24px;
  position: absolute;
  top: 0;
  left: 0;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0.7) 50%, transparent);
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  z-index: 10010;
`;

export { Content };

export const OverlayCaption = styled.div`
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
  line-height: 1;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;

export const DamagePreviewThumbnail = styled.img`
  width: 80px;
  height: 60px;
  cursor: pointer;
  border: 2px solid ${({ theme }) => theme.COLORS.BASE};
  margin: -2px 24px 0 0;
`;

export const OverlayMediaCount = styled.div`
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  color: ${({ theme }) => theme.COLORS.BASE};
  line-height: 1;
`;

export const Image = styled.img`
  width: inherit;
  height: inherit;
  max-height: calc(100vh - 60px);
  max-width: calc(100vw - 336px);
`;

export const Video = styled.video`
  width: inherit;
  height: inherit;
  max-height: calc(100vh - 60px);
  max-width: calc(100vw - 336px);

  &:focus {
    outline: none;
  }
`;

export const DamageMarker = styled.div`
  position: absolute;
  width: 18px;
  height: 18px;
  cursor: pointer;
  background: ${({ theme }) => theme.COLORS.RED};
  border: 2px solid ${({ theme }) => theme.COLORS.BASE};
  box-shadow: 0px 0px 8px #000000;
  border-radius: 50%;
  ${props => props.position};
  z-index: 10020;

  .__react_component_tooltip {
    padding: 0;
    z-index: 10030;
  }
`;

export const DamageMarkerContent = styled.div`
  width: 100%;
  height: 100%;
  padding: 6px;
`;

export const DamageImage = styled.img`
  width: 238px;
`;

export const DamageMarkerLabel = styled.div`
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  text-align: center;
  line-height: 1;
  padding-top: 6px;
`;

export const PlayButtonOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.5);
  transition: all 0.5s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10009;

  ${props => videoOverlayVariant[props.show]};
`;

export const PlayButton = styled.div`
  position: relative;
  width: 12.5%;
  cursor: pointer;
`;
